/**
 * Felles oppslag fra plukklinje → paller (OrderSSCC).
 *
 * Brukes av både usePickingData (chips/teller + batch-badge) og ModernPickingDetails
 * (batch-banner), slik at de tre stedene ikke kan komme i utakt.
 *
 * Se «Claude md files/D-Pak-Pall-Nivaa3/Plan.md» (AP5).
 */

import { PalletSummaryDto, PalletItemDto, OrderBatchNoDto } from '../../../../types/picking';

/** Minimum som trengs for å identifisere en linje, uansett om den er PickingLine eller ExtendedPickingLine. */
export interface PalletLineKey {
  orderNo: number | string;
  /** Ordrelinjens LineNo. Kan mangle på eldre typer. */
  lineNo?: number;
  pListLineNo: number;
  /** Plukket antall — brukes til å fordele ordrelinjens paller på søsken-plukklinjer. */
  qtyPicked?: number;
}

/**
 * Verdien som skal stå i OrderSSCC.LineNo for en plukklinje: **ordrelinjens LineNo**.
 *
 * ERP-eksporten (felt 5 antall, felt 24 lokasjon), GTIN- og vektoppslaget, innholdsetiketten og
 * pallens varenavn kobler alle OrderSSCC mot OrderLine på LineNo. Skrives pListLineNo i stedet,
 * havner pallraden på en annen ordrelinje når nummerseriene er ulike (Haucon 05.10.2026: ordre
 * 775025/775043 fikk forskjøvet antall og lokasjon i plukkvitteringen til Visma).
 * Pallrader eies derfor av ordrelinjen. Plukklinjer som deler ordrelinje (søsken, se
 * {@link getSiblingLines}) får ordrelinjens paller fordelt mellom seg ved lesing.
 */
export function palletRowLineNo(line: PalletLineKey): number {
  return line.lineNo ?? line.pListLineNo;
}

/** Hører pallraden (OrderSSCC / PalletItemDto) til plukklinjens ordrelinje? */
export function isPalletRowForLine(
  row: { orderNo: number | string; lineNo: number },
  line: PalletLineKey
): boolean {
  return Number(row.orderNo) === Number(line.orderNo) && row.lineNo === palletRowLineNo(line);
}

/**
 * Plukklinjene som deler ordrelinje med `line` (inkludert den selv), sortert på pListLineNo.
 * Oppstår i AW-lager når reservasjonen fordeles over flere lokasjoner. Normalt kun linja selv.
 */
export function getSiblingLines<T extends PalletLineKey>(allLines: T[], line: PalletLineKey): T[] {
  return allLines
    .filter(l => Number(l.orderNo) === Number(line.orderNo) && palletRowLineNo(l) === palletRowLineNo(line))
    .sort((a, b) => a.pListLineNo - b.pListLineNo);
}

export interface PalletAllocation {
  sscc: string;
  quantity: number;
  itemBatchNo?: string;
  itemBatchExpDate?: string | null;
  gtin?: string;
}

/**
 * Finner pall-radene som hører til en plukklinje — innenfor samme ordre, på ordrelinjens
 * LineNo (se {@link palletRowLineNo}). Skrivesiden bruker samme nøkkel i alle flyter
 * (skann, blyant, splitt/fordeling, lastbærer-fordeling, D-pak), så matchen er entydig.
 *
 * Med `allLines` fordeles ordrelinjens paller på søsken-plukklinjene i pListLineNo-rekkefølge,
 * begrenset av hver søskens plukkede antall; siste søsken tar resten. Uten søsken (normaltilfellet)
 * er resultatet identisk med ordrelinjens paller. Uten `allLines` returneres ordrelinjens paller.
 */
export function getPalletAllocationsForLine(
  palletSummaries: PalletSummaryDto[],
  line: PalletLineKey,
  allLines?: PalletLineKey[]
): PalletAllocation[] {
  const allocations = getOrderLinePalletAllocations(palletSummaries, line);
  if (!allLines || allocations.length === 0) return allocations;

  const siblings = getSiblingLines(allLines, line);
  if (siblings.length <= 1) return allocations;

  const remaining = allocations.map(a => ({ ...a }));
  for (let i = 0; i < siblings.length; i++) {
    const sibling = siblings[i];
    let capacity = i === siblings.length - 1 ? Number.POSITIVE_INFINITY : Math.max(0, sibling.qtyPicked ?? 0);
    const share: PalletAllocation[] = [];
    for (const allocation of remaining) {
      if (capacity <= 0) break;
      if (allocation.quantity <= 0) continue;
      const take = Math.min(allocation.quantity, capacity);
      allocation.quantity -= take;
      capacity -= take;
      share.push({ ...allocation, quantity: take });
    }
    if (sibling.pListLineNo === line.pListLineNo) return share;
  }
  return [];
}

/** Ordrelinjens paller (summert per pall), uavhengig av hvilken søsken-plukklinje som spør. */
function getOrderLinePalletAllocations(
  palletSummaries: PalletSummaryDto[],
  line: PalletLineKey
): PalletAllocation[] {
  const matches = (item: PalletItemDto): boolean => isPalletRowForLine(item, line);

  const allocations: PalletAllocation[] = [];

  for (const pallet of palletSummaries) {
    const items = pallet.items.filter(matches);
    if (items.length === 0) continue;

    const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
    if (quantity <= 0) continue;

    const withBatch = items.find(item => (item.itemBatchNo || '').trim().length > 0);

    allocations.push({
      sscc: pallet.sscc,
      quantity,
      itemBatchNo: withBatch?.itemBatchNo,
      itemBatchExpDate: withBatch?.itemBatchExpDate ?? null,
      gtin: items.find(item => item.gtin)?.gtin,
    });
  }

  return allocations;
}

/**
 * Antall distinkte batchnummer registrert på linja, på tvers av BEGGE kildene:
 * OrderBatchNo (manuell batch-registrering) og OrderSSCC.ItemBatchNo (D-pak-flyten,
 * og paller beriket manuelt). Uten OrderSSCC-delen står batch-varselet på selv når
 * batchen faktisk er fanget fra etiketten.
 */
export function countRegisteredBatches(
  palletSummaries: PalletSummaryDto[],
  line: PalletLineKey,
  orderBatchNumbers?: OrderBatchNoDto[],
  allLines?: PalletLineKey[]
): number {
  const batches = new Set<string>();

  for (const row of orderBatchNumbers ?? []) {
    const value = (row.batchNo || '').trim();
    if (value) batches.add(value);
  }

  for (const allocation of getPalletAllocationsForLine(palletSummaries, line, allLines)) {
    const value = (allocation.itemBatchNo || '').trim();
    if (value) batches.add(value);
  }

  return batches.size;
}

/**
 * Antallet per pall når alle pallene på linja er like store (typisk D-pak: 12 × 35).
 * Returnerer null når pallene har ulikt antall, slik at kalleren kan droppe «à N».
 */
export function getUniformPalletQuantity(allocations: { quantity: number }[]): number | null {
  if (allocations.length === 0) return null;
  const first = allocations[0].quantity;
  return allocations.every(a => a.quantity === first) ? first : null;
}

/**
 * Visningsnavn for en lastbærer i pallpanelet: «{Type} {nr}», eller «Lastbærer {nr}» uten type
 * (TW 36396317). Nummeret er posisjonen i lista, og lista er sortert i opprettelsesrekkefølge
 * (lagrede paller først i backendens rekkefølge, deretter tomme). Innholdsetikettens
 * «Lastbærer N av M» (TW 36396319) teller på samme måte i backend.
 */
export function getCarrierDisplayName(
  pallets: Pick<PalletSummaryDto, 'sscc' | 'transitPackagingDescr'>[],
  sscc: string,
  fallbackType: string
): string {
  const index = pallets.findIndex(p => p.sscc === sscc);
  const pallet = index >= 0 ? pallets[index] : undefined;
  const type = pallet?.transitPackagingDescr?.trim() || fallbackType;
  return `${type} ${index + 1}`;
}
